/**
 * Fatiado de lib/api.ts por domínio (#131, regra R3).
 */
import { get, post, patch } from './client.js'
// ─── Orders (gestão — cozinha/garçom) ─────────────────────────────────────────

export type OrderItemDetailed = {
  id: string
  order_id: string
  product_id: string
  variant_id: string | null
  variant_label: string | null
  quantity: number
  unit_price_cents: number
  extras_cents: number
  subtotal_cents: number
  is_done: boolean
  notes: string | null
  product_name: string
  removed_ingredients: string[]
  removed_ingredient_ids: string[]
  extras: Array<{
    extra_id: string
    name: string
    quantity: number
    unit_price_cents: number
  }>
}

export type OrderDetailed = {
  id: string
  tenant_id: string
  table_id: string | null
  customer_id: string
  channel: 'dine_in' | 'takeaway' | 'delivery'
  status:
    | 'pending'
    | 'confirmed'
    | 'preparing'
    | 'ready'
    | 'delivered'
    | 'cancelled'
    /** fluxo garçom (0085): enviado → recebido → lançado no sistema local */
    | 'waiter_sent'
    | 'waiter_ack'
    | 'relayed'
  subtotal_cents: number
  tax_cents: number
  tip_cents: number
  total_cents: number
  notes: string | null
  reference_name: string | null
  paid_at: number | null
  // Posição manual no Kanban; null = ordem de chegada
  sort_order: number | null
  created_at: number
  updated_at: number
  table_number: number | null
  table_label: string | null
  customer_name: string | null
  items: OrderItemDetailed[]
  /** pedido preparado em mais de uma estação (regra do "pronto" no KDS) */
  multi_station?: boolean
  /** TODOS os itens do pedido (todas as estações) concluídos? */
  all_items_done?: boolean
}

export const ordersApi = {
  list: (params?: {
    status?: string
    channel?: string
    table_id?: string
    limit?: number
    /** idioma dos nomes dos itens (cascata do cardápio) */
    locale?: string
    /** monitor da cozinha: "null" = principal; uuid = estação */
    kitchen_id?: string
  }) => {
    const q = new URLSearchParams()
    if (params?.status) q.set('status', params.status)
    if (params?.channel) q.set('channel', params.channel)
    if (params?.table_id) q.set('table_id', params.table_id)
    if (params?.kitchen_id) q.set('kitchen_id', params.kitchen_id)
    if (params?.limit) q.set('limit', String(params.limit))
    if (params?.locale) q.set('locale', params.locale)
    const qs = q.toString()
    return get<OrderDetailed[]>(`/orders${qs ? `?${qs}` : ''}`)
  },
  updateStatus: (id: string, status: OrderDetailed['status']) =>
    patch<{ status: OrderDetailed['status'] }>(`/orders/${id}/status`, { status }),
  reorder: (orderIds: string[]) =>
    patch<{ reordered: number }>('/orders/reorder', { order_ids: orderIds }),
  create: (payload: {
    channel: 'dine_in' | 'takeaway' | 'delivery'
    table_id: string | null
    reference_name?: string | null
    notes?: string | null
    tip_cents: number
    items: Array<{
      product_id: string
      variant_id?: string | null
      quantity: number
      removed_ingredient_ids: string[]
      extra_ids: Array<{ extra_id: string; quantity: number }>
    }>
  }) => post<OrderDetailed>('/orders', payload),
  update: (
    id: string,
    patchBody: {
      channel?: 'dine_in' | 'takeaway' | 'delivery'
      table_id?: string | null
      reference_name?: string | null
      notes?: string | null
      items?: Array<{
        /** item gravado que a linha edita (#253: mantém o preço se não mudou) */
        id?: string
        product_id: string
        variant_id?: string | null
        quantity: number
        notes?: string | null
        removed_ingredient_ids: string[]
        extra_ids: Array<{ extra_id: string; quantity: number }>
      }>
    },
  ) => patch<OrderDetailed>(`/orders/${id}`, patchBody),
  setItemDone: (orderId: string, itemId: string, isDone: boolean) =>
    patch<{ is_done: boolean; all_done: boolean }>(`/orders/${orderId}/items/${itemId}`, {
      is_done: isDone,
    }),
  pay: (id: string) => post<{ paid_at: number }>(`/orders/${id}/pay`, {}),
  transfer: (id: string, tableId: string) =>
    post<{
      order_id: string
      from_table_id: string | null
      to_table_id: string
      to_table_number: number
      to_table_label: string | null
    }>(`/orders/${id}/transfer`, { table_id: tableId }),
}
